import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Sparkles, Search, Highlighter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { mockBibleVerses, mockTranslations } from '../mockData';
import { Badge } from './ui/badge';
import * as api from '../services/api';
import StrongsText from './StrongsText';
import StrongsModal from './StrongsModal';
import VerseActionMenu from './VerseActionMenu';
import SocialShareDialog from './SocialShareDialog';
import QuickCompareDialog from './QuickCompareDialog';
import EnhancedVerseImageCustomizer from './EnhancedVerseImageCustomizer';
import AudioBiblePlayer from './AudioBiblePlayer';
import { FuzzySearchInput, SmartSynopsisCard } from './AiBibleTools';
import { toast } from 'sonner';
import { getBookName, isSpanishTranslation } from '../utils/bookTranslations';
import { BIBLE_BOOKS, chapterCountFor, verseCountFor } from '../constants/bibleConstants';
import { useVerseHighlight } from '../hooks/useChapterReader';
import axios from 'axios';
import { LS } from '../utils/lang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HIGHLIGHT_COLORS = {
  yellow: { name: 'Yellow', hex: '#FBBF24', light: '#FEF3C7' },
  green: { name: 'Green', hex: '#10B981', light: '#D1FAE5' },
  blue: { name: 'Blue', hex: '#3B82F6', light: '#DBEAFE' },
  pink: { name: 'Pink', hex: '#EC4899', light: '#FCE7F3' },
  orange: { name: 'Orange', hex: '#F97316', light: '#FFEDD5' },
  pastel_yellow: { name: LS('Pastel Yellow', 'Amarillo pastel'), hex: '#FDE68A', light: '#FEF9C3' },
  pastel_blue: { name: LS('Pastel Blue', 'Azul pastel'), hex: '#93C5FD', light: '#DBEAFE' },
  lavender: { name: 'Lavender', hex: '#C4B5FD', light: '#EDE9FE' },
  peach: { name: 'Peach', hex: '#FDBA74', light: '#FFEDD5' },
  mint: { name: 'Mint', hex: '#6EE7B7', light: '#D1FAE5' }
};

const BibleReader = ({ initialVerse, onVerseChange, onAIAnalyze, isLoadingAI = false }) => {
  const { language } = useLanguage(); // Global language setting
  const [currentVerse, setCurrentVerse] = useState(initialVerse || mockBibleVerses[0]);
  const [selectedTranslation, setSelectedTranslation] = useState(initialVerse?.translation || (language === 'es' ? 'RVG' : 'KJV'));
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStrongsId, setSelectedStrongsId] = useState(null);
  const [isStrongsModalOpen, setIsStrongsModalOpen] = useState(false);
  const [isChapterView, setIsChapterView] = useState(false);
  const [chapterVerses, setChapterVerses] = useState([]);
  
  // Navigation state
  const [selectedBook, setSelectedBook] = useState(currentVerse?.book || 'John');
  const [selectedChapter, setSelectedChapter] = useState(currentVerse?.chapter || 3);
  const [selectedVerse, setSelectedVerse] = useState(currentVerse?.verse || 16);
  const [referenceInput, setReferenceInput] = useState('');
  
  // Verse selection and action menu state (YouVersion-style)
  const [selectedVerseForAction, setSelectedVerseForAction] = useState(null);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isSocialShareOpen, setIsSocialShareOpen] = useState(false);
  const [isVerseImageDialogOpen, setIsVerseImageDialogOpen] = useState(false);
  const [isQuickCompareOpen, setIsQuickCompareOpen] = useState(false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  // Audio narration chapter context — lets "Listen" offer Full-chapter + whole-book
  // (Autoplay next chapter) even when triggered from a single verse.
  const [audioChapterVerses, setAudioChapterVerses] = useState([]);
  const [audioBook, setAudioBook] = useState('');
  const [audioChapter, setAudioChapter] = useState(1);
  const [audioNextChapterToken, setAudioNextChapterToken] = useState(0);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Single-verse highlight state + CRUD (extracted hook).
  const {
    isHighlightDialogOpen, setIsHighlightDialogOpen,
    highlightColor, setHighlightColor,
    highlightNote, setHighlightNote,
    currentHighlight, openHighlightDialog, saveHighlight, removeHighlight,
  } = useVerseHighlight(currentVerse);

  // Honor deep-link URL params on mount — e.g. /bible?book=John&chapter=3&verse=16
  // from Slidecon, email links, or shared chat URLs. Runs ONCE on first render.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const b = sp.get('book');
    const c = parseInt(sp.get('chapter') || '', 10);
    const v = parseInt(sp.get('verse') || '', 10);
    if (b && c && v) {
      loadVerse(b, c, v, selectedTranslation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStrongsClick = (strongsId) => {
    setSelectedStrongsId(strongsId);
    setIsStrongsModalOpen(true);
  };

  // Load verse when navigation changes
  const loadVerse = async (book, chapter, verse, translation) => {
    setIsLoading(true);
    try {
      const reference = `${book} ${chapter}:${verse}`;
      const verseData = await api.getVerse(reference, translation);
      setCurrentVerse(verseData);
      setSelectedBook(book);
      setSelectedChapter(chapter);
      setSelectedVerse(verse);
      
      // Notify parent Dashboard of verse change
      if (onVerseChange) {
        onVerseChange(verseData);
      }
    } catch (error) {
      console.error('Error loading verse:', error);
      toast.error(LS('Could not load verse', 'No se pudo cargar el versículo'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle go to verse button
  const handleGoToVerse = () => {
    loadVerse(selectedBook, selectedChapter, selectedVerse, selectedTranslation);
  };

  // Handle reference search (e.g., "John 3:16")
  const handleReferenceSearch = () => {
    if (!referenceInput.trim()) return;
    
    // Parse reference like "John 3:16"
    const parts = referenceInput.trim().match(/^(\d?\s?[A-Za-z]+)\s+(\d+):(\d+)$/);
    if (parts) {
      const [, book, chapter, verse] = parts;
      loadVerse(book.trim(), parseInt(chapter), parseInt(verse), selectedTranslation);
      setReferenceInput('');
    } else {
      toast.error(LS('Invalid format. Use: Book Chapter:Verse (e.g., John 3:16)', 'Formato inválido. Usa: Libro Capítulo:Versículo (ej. Juan 3:16)'));
    }
  };

  // Refetch content when the translation changes. CRITICAL: when a full chapter
  // is open (Study Mode / chapter view), we must reload chapterVerses too —
  // otherwise the chapter stays stuck in the previous version (the long-standing
  // "stuck in KJV" bug). Also refresh the focused single verse.
  useEffect(() => {
    const refetchInNewTranslation = async () => {
      setIsLoading(true);
      try {
        if (isChapterView) {
          const response = await api.getChapter(selectedBook, selectedChapter, selectedTranslation);
          setChapterVerses(response.verses || []);
        }
        if (currentVerse && selectedTranslation !== currentVerse.translation) {
          const reference = `${currentVerse.book} ${currentVerse.chapter}:${currentVerse.verse}`;
          const verse = await api.getVerse(reference, selectedTranslation);
          setCurrentVerse(verse);
        }
      } catch (error) {
        console.error('Error fetching in new translation:', error);
      } finally {
        setIsLoading(false);
      }
    };

    refetchInNewTranslation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTranslation]);

  const handleNext = () => {
    const book = currentVerse?.book || selectedBook;
    const chapter = currentVerse?.chapter || selectedChapter;
    const verse = currentVerse?.verse || selectedVerse;
    const lastVerse = verseCountFor(book, chapter);
    if (verse < lastVerse) {
      loadVerse(book, chapter, verse + 1, selectedTranslation);
      return;
    }
    // End of chapter → next chapter, else first chapter of next book.
    if (chapter < chapterCountFor(book)) {
      loadVerse(book, chapter + 1, 1, selectedTranslation);
      return;
    }
    const bi = BIBLE_BOOKS.indexOf(book);
    if (bi >= 0 && bi < BIBLE_BOOKS.length - 1) {
      loadVerse(BIBLE_BOOKS[bi + 1], 1, 1, selectedTranslation);
    }
  };

  const handlePrevious = () => {
    const book = currentVerse?.book || selectedBook;
    const chapter = currentVerse?.chapter || selectedChapter;
    const verse = currentVerse?.verse || selectedVerse;
    if (verse > 1) {
      loadVerse(book, chapter, verse - 1, selectedTranslation);
      return;
    }
    // Start of chapter → last verse of previous chapter, else previous book's end.
    if (chapter > 1) {
      loadVerse(book, chapter - 1, verseCountFor(book, chapter - 1), selectedTranslation);
      return;
    }
    const bi = BIBLE_BOOKS.indexOf(book);
    if (bi > 0) {
      const prevBook = BIBLE_BOOKS[bi - 1];
      const lastCh = chapterCountFor(prevBook);
      loadVerse(prevBook, lastCh, verseCountFor(prevBook, lastCh), selectedTranslation);
    }
  };

  const handleAIAnalyze = (verse) => {
    onAIAnalyze(verse);
  };
  

  const loadFullChapter = async () => {
    try {
      setIsLoading(true);
      const response = await api.getChapter(selectedBook, selectedChapter, selectedTranslation);
      setChapterVerses(response.verses);
      setIsChapterView(true);
      toast.success(`${LS('Loaded', 'Cargados')} ${response.total_verses} ${LS('verses from', 'versículos de')} ${getBookName(selectedBook, selectedTranslation, language)} ${selectedChapter}`);
    } catch (error) {
      console.error('Error loading chapter:', error);
      toast.error(`${LS('Failed to load chapter', 'No se pudo cargar el capítulo')}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Group translations by language
  const englishTranslations = mockTranslations.filter(t => t.language === 'English');
  const spanishTranslations = mockTranslations.filter(t => t.language === 'Español');
  
  // Add Strong's versions
  const strongsTranslations = [
    { id: 'kjv-strongs', abbr: 'KJV+Strong\'s', name: 'King James + Strong\'s Numbers', language: 'English' },
    { id: 'rv1909-strongs', abbr: 'RV1909+Strong\'s', name: 'Reina Valera 1909 + Strong\'s', language: 'Español' }
  ];

  // === YouVersion-style Action Handlers ===
  
  const handleVerseClick = (verse) => {
    setSelectedVerseForAction(verse);
    setIsActionMenuOpen(true);
  };

  const handleCloseActionMenu = () => {
    setIsActionMenuOpen(false);
    setSelectedVerseForAction(null);
  };

  // Safety net for Radix stuck body{pointer-events:none} after overlays close.
  useEffect(() => {
    const anyOverlayOpen = isActionMenuOpen || isNoteDialogOpen || isSocialShareOpen ||
      isQuickCompareOpen || isVerseImageDialogOpen || isAudioPlayerOpen;
    if (!anyOverlayOpen) {
      const t = setTimeout(() => {
        if (document.body.style.pointerEvents === 'none') document.body.style.pointerEvents = '';
      }, 120);
      return () => clearTimeout(t);
    }
  }, [isActionMenuOpen, isNoteDialogOpen, isSocialShareOpen, isQuickCompareOpen,
      isVerseImageDialogOpen, isAudioPlayerOpen]);

  const handleSaveVerse = async () => {
    if (!selectedVerseForAction) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/bookmarks`,
        {
          verse_reference: selectedVerseForAction.reference,
          verse_text: selectedVerseForAction.text,
          translation: selectedVerseForAction.translation || selectedTranslation
        },
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Versículo guardado' : 'Verse saved');
      handleCloseActionMenu();
    } catch (error) {
      console.error('Error saving verse:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving verse');
    }
  };

  const handleNoteVerse = () => {
    setNoteText('');
    setIsNoteDialogOpen(true);
    setIsActionMenuOpen(false);  // keep selectedVerseForAction for the dialog
  };

  const handleSaveNote = async () => {
    if (!selectedVerseForAction || !noteText.trim()) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/user/notes`,
        {
          verse_reference: selectedVerseForAction.reference,
          note_text: noteText,
        },
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Nota guardada' : 'Note saved');
      setIsNoteDialogOpen(false);
      setNoteText('');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(language === 'es' ? 'Error al guardar nota' : 'Error saving note');
    }
  };

  const handleCopyVerse = async () => {
    if (!selectedVerseForAction) return;

    const textToCopy = `"${selectedVerseForAction.text}"\n\n${selectedVerseForAction.reference} (${selectedVerseForAction.translation || selectedTranslation})`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(language === 'es' ? 'Copiado al portapapeles' : 'Copied to clipboard');
    } catch (e) {
      toast.error(language === 'es' ? 'No se pudo copiar' : 'Could not copy');
    } finally {
      handleCloseActionMenu();  // always reset state so the menu can reopen
    }
  };

  const handleShareVerse = () => {
    setIsSocialShareOpen(true);
    setIsActionMenuOpen(false);  // Close menu but keep verse data
  };

  const handleImageVerse = () => {
    setIsVerseImageDialogOpen(true);
    setIsActionMenuOpen(false);  // Close menu but keep verse data
  };

  const handleCompareVerse = () => {
    setIsQuickCompareOpen(true);
    setIsActionMenuOpen(false);  // Close menu but keep verse data
  };

  const handleHighlightVerse = () => {
    if (!selectedVerseForAction) return;
    
    // Set current verse to the selected one for highlighting
    setCurrentVerse(selectedVerseForAction);
    openHighlightDialog();
    handleCloseActionMenu();
  };

  const handleAudioVerse = async () => {
    const v = selectedVerseForAction;
    const book = v?.book || selectedBook;
    const chapter = v?.chapter || selectedChapter;
    // Load the whole chapter so the player can offer Full-chapter + whole-book
    // (Autoplay next chapter), then open on the single verse the user tapped.
    try {
      const resp = await api.getChapter(book, chapter, selectedTranslation);
      setAudioChapterVerses(resp.verses || []);
    } catch (e) {
      setAudioChapterVerses([]);
    }
    setAudioBook(book);
    setAudioChapter(chapter);
    setIsAudioPlayerOpen(true);
    setIsActionMenuOpen(false);  // Close menu but keep verse data
  };

  // Whole-book narration: when a chapter finishes with autoplay on, load the next
  // chapter and bump the token so the player resumes at verse 1. False at book end.
  const requestAudioNextChapter = () => {
    const nextCh = audioChapter + 1;
    if (nextCh > chapterCountFor(audioBook)) return false;
    (async () => {
      try {
        const resp = await api.getChapter(audioBook, nextCh, selectedTranslation);
        setAudioChapterVerses(resp.verses || []);
        setAudioChapter(nextCh);
        setAudioNextChapterToken((t) => t + 1);
      } catch (e) { /* stop gracefully */ }
    })();
    return true;
  };

  return (
    <Card className="border-slate-200 shadow-lg">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">
                {getBookName(currentVerse.book, selectedTranslation, language)} {currentVerse.chapter}:{currentVerse.verse}
              </CardTitle>
              <Badge variant="outline" className="mt-1">{selectedTranslation}</Badge>
            </div>
          </div>
          <Select value={selectedTranslation} onValueChange={setSelectedTranslation}>
            <SelectTrigger className="w-[240px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>English</SelectLabel>
                {englishTranslations.map(trans => (
                  <SelectItem key={trans.id} value={trans.abbr}>
                    {trans.abbr} - {trans.name}
                  </SelectItem>
                ))}
                {strongsTranslations.filter(t => t.language === 'English').map(trans => (
                  <SelectItem key={trans.id} value={trans.abbr}>
                    {trans.abbr} - {trans.name}
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>Español</SelectLabel>
                {spanishTranslations.map(trans => (
                  <SelectItem key={trans.id} value={trans.abbr}>
                    {trans.abbr} - {trans.name}
                  </SelectItem>
                ))}
                {strongsTranslations.filter(t => t.language === 'Español').map(trans => (
                  <SelectItem key={trans.id} value={trans.abbr}>
                    {trans.abbr} - {trans.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Bible Navigation Controls */}
        <div className="mb-6 space-y-3">
          {/* AI-powered tools strip (Logos-parity Phase 1):
              fuzzy search + chapter synopsis + book synopsis. */}
          <div
            className="flex flex-wrap items-start gap-2 p-3 rounded-lg border-2 border-dashed border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50/50 to-teal-50/50 dark:from-purple-950/20 dark:to-teal-950/20"
            data-testid="ai-tools-strip"
          >
            <div className="w-full sm:flex-1 min-w-[260px]">
              <FuzzySearchInput language={language} onClose={() => { /* inline, no modal */ }} />
            </div>
            <div className="flex flex-wrap gap-2 items-start">
              <SmartSynopsisCard
                book={currentVerse.book}
                chapter={currentVerse.chapter}
                language={language}
                scope="chapter"
              />
              <SmartSynopsisCard
                book={currentVerse.book}
                language={language}
                scope="book"
              />
            </div>
          </div>

          {/* Quick Reference Input */}
          <div className="flex gap-2">
            <Input
              placeholder={LS('Enter reference (e.g., John 3:16)', 'Escribe una referencia (ej. Juan 3:16)')}
              value={referenceInput}
              onChange={(e) => setReferenceInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleReferenceSearch()}
              className="flex-1"
            />
            <Button onClick={handleReferenceSearch} size="sm">
              <Search className="w-4 h-4 mr-1" />
              {LS('Go', 'Ir')}
            </Button>
          </div>

          {/* Book, Chapter, Verse Selectors */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {/* Book Selector */}
            <Select value={selectedBook} onValueChange={setSelectedBook}>
              <SelectTrigger>
                <SelectValue placeholder={LS('Select Book', 'Selecciona un libro')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectGroup>
                  <SelectLabel>{LS('Old Testament', 'Antiguo Testamento')}</SelectLabel>
                  {BIBLE_BOOKS.slice(0, 39).map(book => (
                    <SelectItem key={book} value={book}>{getBookName(book, selectedTranslation, language)}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>{LS('New Testament', 'Nuevo Testamento')}</SelectLabel>
                  {BIBLE_BOOKS.slice(39).map(book => (
                    <SelectItem key={book} value={book}>{getBookName(book, selectedTranslation, language)}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Chapter Selector */}
            <Select value={String(selectedChapter)} onValueChange={(val) => setSelectedChapter(parseInt(val))}>
              <SelectTrigger>
                <SelectValue placeholder={LS('Chapter', 'Capítulo')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {Array.from({ length: chapterCountFor(selectedBook) }, (_, i) => i + 1).map(num => (
                  <SelectItem key={num} value={String(num)}>
                    {LS('Chapter', 'Capítulo')} {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Verse Selector */}
            <Select value={String(selectedVerse)} onValueChange={(val) => setSelectedVerse(parseInt(val))}>
              <SelectTrigger>
                <SelectValue placeholder={LS('Verse', 'Versículo')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {Array.from({ length: verseCountFor(selectedBook, selectedChapter) }, (_, i) => i + 1).map(num => (
                  <SelectItem key={num} value={String(num)}>
                    {LS('Verse', 'Versículo')} {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Load Buttons */}
          <div className="flex gap-2">
            <Button onClick={handleGoToVerse} className="flex-1" variant="default">
              <BookOpen className="w-4 h-4 mr-2" />
              {LS('Load', 'Cargar')} {getBookName(selectedBook, selectedTranslation, language)} {selectedChapter}:{selectedVerse}
            </Button>
            <Button onClick={loadFullChapter} variant="outline" className="flex-1">
              📖 {LS('Read Chapter', 'Leer capítulo')}
            </Button>
          </div>
        </div>

        {/* Verse/Chapter Display */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">{LS('Loading...', 'Cargando...')}</p>
          </div>
        ) : isChapterView ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{getBookName(selectedBook, selectedTranslation, language)} {selectedChapter}</h2>
              <Button onClick={() => setIsChapterView(false)} variant="outline" size="sm">
                {language === 'es' ? 'Volver a Versículo Único' : 'Back to Single Verse'}
              </Button>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {chapterVerses.map((verse) => (
                <div 
                  key={verse.verse} 
                  className="flex gap-2 group hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded cursor-pointer transition-colors"
                  onClick={() => handleVerseClick({
                    ...verse,
                    reference: `${selectedBook} ${selectedChapter}:${verse.verse}`,
                    translation: selectedTranslation
                  })}
                >
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400 min-w-[30px] select-none">
                    {verse.verse_end && verse.verse_end > verse.verse ? `${verse.verse}-${verse.verse_end}` : verse.verse}
                  </span>
                  <p className="text-base leading-relaxed text-slate-700 dark:text-slate-300">
                    {verse.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-8">
            <div 
              className={`p-4 rounded-lg transition-all cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${currentHighlight ? 'border-l-4' : ''}`}
              style={currentHighlight ? { borderLeftColor: HIGHLIGHT_COLORS[currentHighlight.color].hex } : {}}
              onClick={() => handleVerseClick(currentVerse)}
            >
              <p className="text-lg leading-relaxed text-foreground dark:text-slate-100">
                {currentHighlight ? (
                  <span
                    style={{
                      backgroundColor: HIGHLIGHT_COLORS[currentHighlight.color].light,
                      color: '#0f172a',
                      borderRadius: '3px',
                      padding: '0 2px',
                      WebkitBoxDecorationBreak: 'clone',
                      boxDecorationBreak: 'clone',
                    }}
                  >
                    <StrongsText 
                      text={currentVerse.text} 
                      onStrongsClick={handleStrongsClick}
                    />
                  </span>
                ) : (
                  <StrongsText 
                    text={currentVerse.text} 
                    onStrongsClick={handleStrongsClick}
                  />
                )}
              </p>
              {currentHighlight?.note && (
                <div className="mt-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded border border-slate-300 dark:border-slate-600">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">📝 Note:</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{currentHighlight.note}</p>
                </div>
              )}
            </div>
            {currentVerse.has_strongs && (
              <p className="text-xs text-blue-600 mt-3">
                💡 Click on the blue Strong's numbers to see original Hebrew/Greek definitions
              </p>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-slate-700">
          <Button
            variant="outline"
            onClick={handlePrevious}
            className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            data-testid="verse-nav-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {LS('Previous', 'Anterior')}
          </Button>
          
          <div className="flex gap-2">
            <Button
              onClick={openHighlightDialog}
              variant={currentHighlight ? "default" : "outline"}
              className={currentHighlight ? "" : "border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"}
              style={currentHighlight ? {
                backgroundColor: HIGHLIGHT_COLORS[currentHighlight.color].hex,
                color: 'white'
              } : {}}
            >
              <Highlighter className="w-4 h-4 mr-2" />
              {currentHighlight ? LS('Edit', 'Editar') : LS('Highlight', 'Resaltar')}
            </Button>

            <Button
              onClick={() => onAIAnalyze(currentVerse)}
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 transition-all duration-200"
              disabled={isLoadingAI}
            >
              {isLoadingAI ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {LS('Analyzing...', 'Analizando...')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {LS('AI Insights', 'Perspectivas IA')}
                </>
              )}
            </Button>
          </div>
          
          <Button
            variant="outline"
            onClick={handleNext}
            className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            data-testid="verse-nav-next-btn"
          >
            {LS('Next', 'Siguiente')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
      
      <StrongsModal
        strongsId={selectedStrongsId}
        isOpen={isStrongsModalOpen}
        onClose={() => setIsStrongsModalOpen(false)}
        translation={selectedTranslation}
      />

      {/* Highlight Dialog */}
      <Dialog open={isHighlightDialogOpen} onOpenChange={setIsHighlightDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{LS('Highlight Verse', 'Resaltar versículo')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded border">
              <p className="text-sm font-semibold mb-1">{currentVerse.reference}</p>
              <p className="text-sm italic">"{currentVerse.text}"</p>
            </div>

            <div className="space-y-2">
              <Label>{LS('Choose Color', 'Elige un color')}</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(HIGHLIGHT_COLORS).map(([key, color]) => (
                  <button
                    key={key}
                    onClick={() => setHighlightColor(key)}
                    className={`w-10 h-10 rounded-full border-4 transition-all ${
                      highlightColor === key ? 'scale-110 border-slate-800 dark:border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="highlight-note">{LS('Add Note (Optional)', 'Agregar nota (opcional)')}</Label>
              <Textarea
                id="highlight-note"
                value={highlightNote}
                onChange={(e) => setHighlightNote(e.target.value)}
                placeholder={LS('Add your thoughts, reflections, or prayers...', 'Agrega tus pensamientos, reflexiones u oraciones...')}
                rows={4}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={saveHighlight} className="flex-1">
                {currentHighlight ? 'Update Highlight' : 'Save Highlight'}
              </Button>
              {currentHighlight && (
                <Button onClick={removeHighlight} variant="outline" className="text-red-600 hover:bg-red-50">
                  {LS('Remove', 'Quitar')}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* YouVersion-style Verse Action Menu */}
      {isActionMenuOpen && selectedVerseForAction && (
        <VerseActionMenu
          isOpen={isActionMenuOpen}
          verse={selectedVerseForAction}
          onClose={handleCloseActionMenu}
          onSave={handleSaveVerse}
          onNote={handleNoteVerse}
          onCopy={handleCopyVerse}
          onShare={handleShareVerse}
          onImage={handleImageVerse}
          onCompare={handleCompareVerse}
          onHighlight={handleHighlightVerse}
          onAudio={handleAudioVerse}
        />
      )}

      {/* Social Share Dialog */}
      {selectedVerseForAction && (
        <>
          <SocialShareDialog
            isOpen={isSocialShareOpen}
            onClose={() => setIsSocialShareOpen(false)}
            verse={selectedVerseForAction}
          />

          {/* Quick Compare Dialog */}
          <QuickCompareDialog
            isOpen={isQuickCompareOpen}
            onClose={() => {
              setIsQuickCompareOpen(false);
            }}
            verse={selectedVerseForAction}
          />

          {/* Enhanced Verse Image Customizer */}
          <EnhancedVerseImageCustomizer
            isOpen={isVerseImageDialogOpen}
            onClose={() => {
              setIsVerseImageDialogOpen(false);
            }}
            verse={selectedVerseForAction}
          />

          {/* Verse Audio Player */}
          <AudioBiblePlayer
            isOpen={isAudioPlayerOpen}
            onClose={() => {
              setIsAudioPlayerOpen(false);
            }}
            verse={selectedVerseForAction}
            chapterVerses={audioChapterVerses}
            translation={selectedTranslation}
            initialScope="verse"
            onRequestNextChapter={requestAudioNextChapter}
            nextChapterToken={audioNextChapterToken}
          />
        </>
      )}

      {/* Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={(v) => { setIsNoteDialogOpen(v); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Agregar Nota' : 'Add Note'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {selectedVerseForAction?.reference}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 italic">
                "{selectedVerseForAction?.text}"
              </p>
            </div>
            <div>
              <Label htmlFor="verse-note">
                {language === 'es' ? 'Tu Nota' : 'Your Note'}
              </Label>
              <Textarea
                id="verse-note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={language === 'es' ? 'Escribe tus reflexiones...' : 'Write your reflections...'}
                rows={6}
                className="mt-2"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveNote} className="flex-1" disabled={!noteText.trim()}>
                {language === 'es' ? 'Guardar Nota' : 'Save Note'}
              </Button>
              <Button onClick={() => setIsNoteDialogOpen(false)} variant="outline">
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BibleReader;
